const moment = require('moment-timezone')

const COPENHAGEN_TIME_ZONE = 'Europe/Copenhagen'
const DEFAULT_HIGH_SEASON_START = 3
const DEFAULT_HIGH_SEASON_END = 8
const BOOKING_DATE_FORMATS = [
  'YYYY-MM-DD',
  'MMMM D, YYYY',
  'DD.MM.YYYY'
]

const normalizeBookingCutoff = (value) => {
  if (value === '' || value === null || value === undefined) return ''

  const normalizedValue = String(value).trim().padStart(4, '0')
  if (!/^\d{4}$/.test(normalizedValue)) return ''

  const hours = Number(normalizedValue.slice(0, 2))
  const minutes = Number(normalizedValue.slice(2))
  if (hours > 23 || minutes > 59) return ''

  return normalizedValue
}

const getBookingOffsetDays = ({
  copenhagenNow,
  lastTime,
  todayBookingAllowed,
  highSeasonStart,
  highSeasonEnd
}) => {
  const normalizedLastTime = normalizeBookingCutoff(lastTime)
  if (normalizedLastTime) {
    const cutoff = parseInt(normalizedLastTime, 10)
    const cutoffMinutes = Math.floor(cutoff / 100) * 60 + (cutoff % 100)
    const currentMinutes = copenhagenNow.hours() * 60 + copenhagenNow.minutes()

    return currentMinutes < cutoffMinutes ? 0 : 1
  }

  if (todayBookingAllowed) return 0

  const isHighSeason = copenhagenNow.month() >= highSeasonStart &&
    copenhagenNow.month() <= highSeasonEnd

  if (isHighSeason) {
    if (copenhagenNow.hours() >= 12) return 2

    const dayOfWeek = copenhagenNow.day()
    return dayOfWeek === 5 || dayOfWeek === 6 ? 1 : 2
  }

  return copenhagenNow.hours() >= 12 ? 3 : 2
}

const getMinimumBookingDate = ({
  now = new Date(),
  lastTime = '',
  todayBookingAllowed = false,
  highSeasonStart = DEFAULT_HIGH_SEASON_START,
  highSeasonEnd = DEFAULT_HIGH_SEASON_END
} = {}) => {
  const copenhagenNow = moment.tz(now, COPENHAGEN_TIME_ZONE)
  const offsetDays = getBookingOffsetDays({
    copenhagenNow,
    lastTime,
    todayBookingAllowed,
    highSeasonStart,
    highSeasonEnd
  })

  // jQuery treats numeric minDate values as offsets from the browser's local date.
  // Return an exact Copenhagen calendar date so every browser gets the same day.
  return copenhagenNow
    .clone()
    .startOf('day')
    .add(offsetDays, 'days')
    .format('YYYY-MM-DD')
}

const getCopenhagenDate = ({
  now = new Date(),
  format = 'YYYY-MM-DD'
} = {}) => moment.tz(now, COPENHAGEN_TIME_ZONE).format(format)

const normalizeBookingDate = (date) => {
  if (typeof date !== 'string') return ''

  const parsedDate = moment.utc(date.trim(), BOOKING_DATE_FORMATS, true)
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : ''
}

const getCalendarDayDifference = (startDate, endDate) => {
  const normalizedStartDate = normalizeBookingDate(startDate)
  const normalizedEndDate = normalizeBookingDate(endDate)
  if (!normalizedStartDate || !normalizedEndDate) return null

  const toUtcDay = (date) => {
    const [year, month, day] = date.split('-').map(Number)
    return Date.UTC(year, month - 1, day)
  }

  return Math.round(
    (toUtcDay(normalizedEndDate) - toUtcDay(normalizedStartDate)) /
    (24 * 60 * 60 * 1000)
  )
}

const isCopenhagenDateAvailable = (availableDates, { now = new Date() } = {}) => {
  if (!Array.isArray(availableDates) || !availableDates.length) return true

  const normalizedAvailableDates = availableDates
    .map(normalizeBookingDate)
    .filter(Boolean)

  return normalizedAvailableDates.includes(getCopenhagenDate({ now }))
}

const isHighSeasonBookingDate = (
  date,
  highSeasonStart = DEFAULT_HIGH_SEASON_START,
  highSeasonEnd = DEFAULT_HIGH_SEASON_END
) => {
  const normalizedDate = normalizeBookingDate(date)
  if (!normalizedDate) return false

  const monthIndex = Number(normalizedDate.slice(5, 7)) - 1
  return monthIndex >= highSeasonStart && monthIndex <= highSeasonEnd
}

const isBookingDateAllowed = ({ date, ...minimumDateOptions } = {}) => {
  const normalizedDate = normalizeBookingDate(date)
  if (!normalizedDate) return false

  return normalizedDate >= getMinimumBookingDate(minimumDateOptions)
}

const formatBookingDateForDisplay = (date) => {
  const normalizedDate = normalizeBookingDate(date)
  if (!normalizedDate) return ''

  return moment.utc(normalizedDate, 'YYYY-MM-DD', true).format('MMMM D, YYYY')
}

const formatBookingDateForApi = (date) => {
  const normalizedDate = normalizeBookingDate(date)
  if (!normalizedDate) return ''

  const [year, month, day] = normalizedDate.split('-')
  return `${day}.${month}.${year}`
}

module.exports = {
  COPENHAGEN_TIME_ZONE,
  formatBookingDateForApi,
  formatBookingDateForDisplay,
  getCalendarDayDifference,
  getCopenhagenDate,
  getMinimumBookingDate,
  isBookingDateAllowed,
  isCopenhagenDateAvailable,
  isHighSeasonBookingDate,
  normalizeBookingCutoff,
  normalizeBookingDate
}
